import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp, Package, AlertTriangle, BarChart3,
  ChevronDown, ArrowUpRight, ArrowDownRight, ShoppingCart,
} from 'lucide-react';
import {
  Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ComposedChart, Cell,
} from 'recharts';
import { PageHeader, SectionCard, StatusBadge, EmptyState } from '../components/ui';
import { API_BASE } from '../api';
import { useDemo } from '../context/DemoContext';
import { DEMO_VENDOR_FORECASTS } from '../demo/data';

// ── Types ──────────────────────────────────────────────────────────────────

interface ForecastMonth {
  month: string;
  label: string;
  revenue: number;
  units: number;
  recommended_production: number;
  status: 'on_track' | 'at_risk' | 'critical';
}

interface ProductForecast {
  product_id: string;
  product_title: string;
  current_stock: number;
  projected_demand: number;
  surplus_deficit: number;
  days_of_supply: number;
}

interface ForecastData {
  annual_revenue: number;
  annual_units: number;
  growth_rate: number;
  months: ForecastMonth[];
  product_forecasts: ProductForecast[];
}

// ── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `$${(n / 1_000).toFixed(1)}K`
    : `$${n.toLocaleString()}`;

const fmtFull = (n: number) =>
  `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const statusVariant = (s: string) => {
  if (s === 'on_track') return 'success' as const;
  if (s === 'at_risk') return 'warning' as const;
  return 'error' as const;
};

// ── Recharts Tooltips ──────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="px-3 py-2 text-xs"
      style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}
    >
      <p className="text-white/50 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }} className="font-semibold">
          {p.dataKey === 'revenue' ? fmtFull(p.value) : `${p.value} units`}
        </p>
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function VendorForecasts() {
  const { t } = useTranslation(['forecasts', 'common']);
  const { isDemoMode } = useDemo();

  const [data, setData] = useState<ForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const monthRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const currentMonthIndex = new Date().getMonth();

  useEffect(() => {
    setLoading(true);

    if (isDemoMode && DEMO_VENDOR_FORECASTS) {
      setData(DEMO_VENDOR_FORECASTS as unknown as ForecastData);
      setLoading(false);
      return;
    }

    fetch(`${API_BASE}/vendor/forecasts`, { credentials: 'include' })
      .then((r) => r.json())
      .then((res) => setData(res.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isDemoMode]);

  // Chart data with translated month labels
  const chartData = useMemo(() => {
    if (!data?.months) return [];
    return data.months.map((m) => ({
      ...m,
      name: t(`forecasts:months.${m.label}`),
    }));
  }, [data, t]);

  // Filter product forecasts
  const filteredProducts = useMemo(() => {
    if (!data?.product_forecasts) return [];
    if (selectedProduct === 'all') return data.product_forecasts;
    return data.product_forecasts.filter((p) => p.product_id === selectedProduct);
  }, [data, selectedProduct]);

  // Products at stockout risk (< 30 days of supply)
  const stockoutRisks = useMemo(() => {
    if (!data?.product_forecasts) return [];
    return data.product_forecasts
      .filter((p) => p.days_of_supply < 30)
      .sort((a, b) => a.days_of_supply - b.days_of_supply);
  }, [data]);

  const scrollToMonth = (monthKey: string) => {
    const el = monthRefs.current[monthKey];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // ── Loading ──

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('forecasts:title')} subtitle={t('forecasts:subtitle')} />
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse" style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 4 }} />
          ))}
        </div>
        <div className="h-72 animate-pulse" style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 4 }} />
      </div>
    );
  }

  // ── Empty State ──

  if (!data) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('forecasts:title')} subtitle={t('forecasts:subtitle')} />
        <SectionCard>
          <EmptyState
            icon={<BarChart3 className="w-6 h-6" />}
            title={t('forecasts:title')}
            message={t('forecasts:noData')}
          />
        </SectionCard>
      </div>
    );
  }

  // ── Render ──

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('forecasts:title')}
        subtitle={t('forecasts:subtitle')}
        actions={
          <MonthJumpDropdown
            months={data.months}
            t={t}
            onSelect={scrollToMonth}
          />
        }
      />

      {/* ── Top Stats ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatTile
          label={t('forecasts:annualRevenue')}
          value={fmt(data.annual_revenue)}
          icon={<TrendingUp className="w-4 h-4" />}
          color="#FFD400"
        />
        <StatTile
          label={t('forecasts:annualUnits')}
          value={data.annual_units.toLocaleString()}
          icon={<Package className="w-4 h-4" />}
          color="#4ade80"
        />
        <StatTile
          label={t('forecasts:growthRate')}
          value={`+${data.growth_rate}%`}
          icon={<ArrowUpRight className="w-4 h-4" />}
          color="#a78bfa"
        />
      </div>

      {/* ── Product Filter ── */}
      <div className="flex items-center gap-3">
        <label className="text-xs text-white/40 uppercase tracking-wider">
          {t('forecasts:productFilter')}
        </label>
        <select
          value={selectedProduct}
          onChange={(e) => setSelectedProduct(e.target.value)}
          className="text-sm px-3 py-1.5 bg-white/[0.04] text-white border border-white/[0.08] outline-none focus:border-[#FFD400]/40 transition-colors"
          style={{ borderRadius: 2 }}
        >
          <option value="all" className="bg-[#1a1a1a]">{t('forecasts:allProducts')}</option>
          {data.product_forecasts.map((p) => (
            <option key={p.product_id} value={p.product_id} className="bg-[#1a1a1a]">
              {p.product_title}
            </option>
          ))}
        </select>
      </div>

      {/* ── Annual Overview Chart ── */}
      <SectionCard title={t('forecasts:annualOverview')}>
        <div className="p-4" style={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis
                dataKey="name"
                tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.06)' }}
                tickLine={false}
              />
              <YAxis
                yAxisId="revenue"
                tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => fmt(v)}
              />
              <YAxis
                yAxisId="units"
                orientation="right"
                tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.02)' }} />
              <Bar
                yAxisId="revenue"
                dataKey="revenue"
                radius={[2, 2, 0, 0]}
                maxBarSize={36}
              >
                {chartData.map((_, i) => (
                  <Cell
                    key={i}
                    fill={i === currentMonthIndex ? '#FFD400' : 'rgba(255,212,0,0.45)'}
                  />
                ))}
              </Bar>
              <Line
                yAxisId="units"
                type="monotone"
                dataKey="units"
                stroke="#4ade80"
                strokeWidth={2}
                dot={{ r: 3, fill: '#4ade80', strokeWidth: 0 }}
                activeDot={{ r: 5, fill: '#4ade80', strokeWidth: 0 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-5 px-5 pb-4 text-xs text-white/40">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-2.5 inline-block" style={{ backgroundColor: '#FFD400', borderRadius: 1 }} />
            {t('forecasts:revenue')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 inline-block" style={{ backgroundColor: '#4ade80' }} />
            {t('forecasts:units')}
          </span>
        </div>
      </SectionCard>

      {/* ── Monthly Detail Cards ── */}
      <SectionCard title={t('forecasts:monthlyDetail')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-4">
          {data.months.map((m, i) => {
            const isCurrent = i === currentMonthIndex;
            return (
              <div
                key={m.month}
                ref={(el) => { monthRefs.current[m.label] = el; }}
                className="p-4 relative"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.025)',
                  border: `1px solid ${isCurrent ? '#FFD400' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: 4,
                }}
              >
                {isCurrent && (
                  <span
                    className="absolute top-2 right-2 text-[10px] font-bold px-1.5 py-0.5"
                    style={{ backgroundColor: 'rgba(255,212,0,0.15)', color: '#FFD400', borderRadius: 2 }}
                  >
                    {t('forecasts:currentMonth')}
                  </span>
                )}

                <p className="text-sm font-semibold text-white mb-3">
                  {t(`forecasts:months.${m.label}`)}
                </p>

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-white/40">{t('forecasts:expectedRevenue')}</span>
                    <span className="text-white font-semibold">{fmtFull(m.revenue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">{t('forecasts:expectedUnits')}</span>
                    <span className="text-white/70">{m.units.toLocaleString()} {t('forecasts:units').toLowerCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">{t('forecasts:recommendedProduction')}</span>
                    <span className="text-white/70">{m.recommended_production.toLocaleString()}</span>
                  </div>
                </div>

                <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <StatusBadge variant={statusVariant(m.status)}>
                    {m.status === 'on_track' ? t('forecasts:onTrack')
                      : m.status === 'at_risk' ? t('forecasts:atRisk')
                      : t('forecasts:critical')}
                  </StatusBadge>
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* ── Production Planning Table ── */}
      <SectionCard
        title={t('forecasts:productionPlanning')}
        subtitle={t('forecasts:productionPlanSubtitle')}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <colgroup>
              <col style={{ width: '28%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left px-4 py-3 text-xs text-white/40 uppercase tracking-wider">
                  {t('forecasts:product')}
                </th>
                <th className="text-right px-4 py-3 text-xs text-white/40 uppercase tracking-wider">
                  {t('forecasts:currentStock')}
                </th>
                <th className="text-right px-4 py-3 text-xs text-white/40 uppercase tracking-wider">
                  {t('forecasts:projectedDemand')}
                </th>
                <th className="text-right px-4 py-3 text-xs text-white/40 uppercase tracking-wider">
                  {t('forecasts:surplusDeficit')}
                </th>
                <th className="text-center px-4 py-3 text-xs text-white/40 uppercase tracking-wider">
                  {t('forecasts:action')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((p) => {
                const isDeficit = p.surplus_deficit < 0;
                return (
                  <tr key={p.product_id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 text-white/80 font-medium truncate max-w-[220px]">
                      {p.product_title}
                    </td>
                    <td className="px-4 py-3 text-right text-white/50 tabular-nums">
                      {p.current_stock.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-white/50 tabular-nums">
                      {p.projected_demand.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className="inline-flex items-center gap-1 font-semibold tabular-nums"
                        style={{ color: isDeficit ? '#f87171' : '#4ade80' }}
                      >
                        {isDeficit
                          ? <ArrowDownRight className="w-3.5 h-3.5" />
                          : <ArrowUpRight className="w-3.5 h-3.5" />}
                        {isDeficit ? '' : '+'}{p.surplus_deficit.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isDeficit ? (
                        <button
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 transition-colors"
                          style={{
                            backgroundColor: 'rgba(255,212,0,0.1)',
                            color: '#FFD400',
                            border: '1px solid rgba(255,212,0,0.2)',
                            borderRadius: 2,
                          }}
                        >
                          <ShoppingCart className="w-3 h-3" />
                          {t('forecasts:orderNow')}
                        </button>
                      ) : (
                        <StatusBadge variant="success">
                          {t('forecasts:sufficient')}
                        </StatusBadge>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-white/30">
                    {t('common:noResults')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* ── Stockout Risk Section ── */}
      {stockoutRisks.length > 0 && (
        <SectionCard
          title={t('forecasts:stockoutRisk')}
          subtitle={t('forecasts:stockoutRiskSubtitle')}
        >
          <div className="divide-y divide-white/[0.04]">
            {stockoutRisks.map((p) => {
              const urgent = p.days_of_supply < 14;
              return (
                <div
                  key={p.product_id}
                  className="flex items-center justify-between px-5 py-4 hover:bg-white/[0.015] transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-8 h-8 shrink-0 flex items-center justify-center"
                      style={{
                        backgroundColor: urgent ? 'rgba(248,113,113,0.1)' : 'rgba(234,179,8,0.1)',
                        color: urgent ? '#f87171' : '#eab308',
                        borderRadius: 2,
                      }}
                    >
                      <AlertTriangle className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{p.product_title}</p>
                      <p className="text-xs text-white/35 mt-0.5">
                        {t('forecasts:daysRemaining', { count: p.days_of_supply })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="text-xs text-white/40">{t('forecasts:daysOfSupply')}</p>
                      <p
                        className="text-sm font-bold tabular-nums"
                        style={{ color: urgent ? '#f87171' : '#eab308' }}
                      >
                        {p.days_of_supply}
                      </p>
                    </div>
                    <div className="h-8 w-px" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
                    <div className="text-right">
                      <p className="text-xs text-white/40">{t('forecasts:deficit')}</p>
                      <p className="text-sm font-bold text-[#f87171] tabular-nums">
                        {p.surplus_deficit.toLocaleString()}
                      </p>
                    </div>
                    <button
                      className="text-xs font-semibold px-3 py-1.5 ml-2 transition-colors"
                      style={{
                        backgroundColor: urgent ? 'rgba(248,113,113,0.1)' : 'rgba(255,212,0,0.1)',
                        color: urgent ? '#f87171' : '#FFD400',
                        border: `1px solid ${urgent ? 'rgba(248,113,113,0.2)' : 'rgba(255,212,0,0.2)'}`,
                        borderRadius: 2,
                      }}
                    >
                      {t('forecasts:orderNow')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function StatTile({
  label, value, icon, color,
}: {
  label: string; value: string; icon: React.ReactNode; color: string;
}) {
  return (
    <div
      className="p-4"
      style={{ backgroundColor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-7 h-7 flex items-center justify-center"
          style={{ backgroundColor: `${color}15`, color, borderRadius: 2 }}
        >
          {icon}
        </div>
      </div>
      <p className="text-xl font-bold text-white">{value}</p>
      <p className="text-xs text-white/40 mt-0.5">{label}</p>
    </div>
  );
}

function MonthJumpDropdown({
  months, t, onSelect,
}: {
  months: ForecastMonth[];
  t: ReturnType<typeof useTranslation>['t'];
  onSelect: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="btn-ghost flex items-center gap-2 text-sm"
      >
        {t('forecasts:jumpToMonth')}
        <ChevronDown className="w-4 h-4" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 py-1 min-w-[160px] shadow-xl"
          style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2,
          }}
        >
          {months.map((m) => (
            <button
              key={m.month}
              onClick={() => { onSelect(m.label); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-white/70 hover:text-white hover:bg-white/[0.05] transition-colors"
            >
              {t(`forecasts:months.${m.label}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
